import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const W = 360, H = 400, GAP = 120, PIPE_W = 50, GRAV = 0.45, FLAP = -7;

export function FlappyBlock({ onScore }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const state = useRef(null);
  const reported = useRef(false);

  const start = () => {
    state.current = { y: H / 2, v: 0, pipes: [{ x: W, top: 120 }], score: 0 };
    setScore(0); setOver(false); setRunning(true); reported.current = false;
  };

  const flap = useCallback(() => { if (state.current) state.current.v = FLAP; }, []);

  useEffect(() => {
    if (!running) return;
    const ctx = canvasRef.current.getContext("2d");
    let raf, frame = 0;
    const loop = () => {
      const s = state.current;
      s.v += GRAV; s.y += s.v;
      frame++;
      if (frame % 90 === 0) s.pipes.push({ x: W, top: 60 + Math.random() * (H - GAP - 120) });
      s.pipes.forEach((p) => (p.x -= 2.4));
      s.pipes = s.pipes.filter((p) => p.x > -PIPE_W);
      // score + collision
      for (const p of s.pipes) {
        if (!p.passed && p.x + PIPE_W < 60) { p.passed = true; s.score += 1; setScore(s.score); }
        if (60 + 14 > p.x && 60 - 14 < p.x + PIPE_W && (s.y - 14 < p.top || s.y + 14 > p.top + GAP)) {
          setRunning(false); setOver(true); setScore(s.score);
        }
      }
      if (s.y > H - 14 || s.y < 14) { setRunning(false); setOver(true); setScore(s.score); }
      // draw
      ctx.fillStyle = "#0b1020"; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#39FF14";
      for (const p of s.pipes) { ctx.fillRect(p.x, 0, PIPE_W, p.top); ctx.fillRect(p.x, p.top + GAP, PIPE_W, H); }
      ctx.fillStyle = "#EFFF04"; ctx.fillRect(60 - 14, s.y - 14, 28, 28);
      ctx.fillStyle = "#fff"; ctx.font = "bold 28px monospace"; ctx.textAlign = "center"; ctx.fillText(s.score, W / 2, 50);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const key = (e) => { if (e.code === "Space") { e.preventDefault(); flap(); } };
    window.addEventListener("keydown", key);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("keydown", key); };
  }, [running, flap]);

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 100); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="flappy-game">
      <div className="relative border-2 border-zinc-700" style={{ width: "min(92vw, 360px)" }}>
        <canvas ref={canvasRef} width={W} height={H} onClick={flap} className="w-full block cursor-pointer" />
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="flappy-over">Crashed! Score {score}</p>}
            <Button onClick={start} data-testid="flappy-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Retry" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Click or press SPACE to flap. Avoid the pipes!</p>
    </div>
  );
}
